import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  MessageCircle,
  FileCheck2,
  Users,
  Video,
  Brain,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Calendar,
  Clock,
  Globe2,
  HeartHandshake,
  PhoneCall,
  Headphones,
  Target,
  RefreshCw,
} from "lucide-react";
import { site } from "@/lib/site";
import { GroupSignupDialog } from "@/components/site/GroupSignupDialog";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Services - WeLivdIt US Visa Coaching" },
      {
        name: "description",
        content:
          "1-on-1 mock interviews, DS-160 review, refusal recovery and a free WhatsApp community - everything you need before your US visa interview.",
      },
      { property: "og:title", content: "Services - WeLivdIt US Visa Coaching" },
      {
        property: "og:description",
        content:
          "Mock interviews, DS-160 review, refusal recovery, and free WhatsApp community for US visa applicants.",
      },
      { property: "og:url", content: "https://welivdit.com/services" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": services.map((s) => ({
            "@type": "Service",
            name: s.title,
            description: s.desc,
            provider: { "@type": "Organization", name: "WeLivdIt" },
            areaServed: "Worldwide",
          })),
        }),
      },
    ],
  }),
  component: ServicesPage,
});

const services = [
  {
    icon: MessageCircle,
    title: "1-on-1 Mock Interviews",
    desc: "A senior Specialist plays the visa officer. You answer real questions. We give you specific, line-by-line feedback on what to fix.",
  },
  {
    icon: FileCheck2,
    title: "DS-160 Form Review",
    desc: "Every field cross-checked against your I-20, SEVIS, and financials. We flag inconsistencies before the consulate does.",
  },
  {
    icon: Brain,
    title: "Visa Officer Logic Training",
    desc: "Understand what officers actually listen for - 214(b) intent, ties to home, financial credibility - and how to satisfy each in two sentences.",
  },
  {
    icon: Video,
    title: "Spontaneous Question Drills",
    desc: "We throw curveballs your friends won't think of. The goal: nothing in the actual interview surprises you.",
  },
  {
    icon: RefreshCw,
    title: "Refusal Root-Cause Analysis",
    desc: "Walked away with a 214(b)? We deconstruct exactly what went wrong, identify the red flags, and rebuild your story for the re-attempt.",
  },
  {
    icon: ShieldCheck,
    title: "Interview-Day Prep",
    desc: "Body language, document order, what to carry, opening line. The 24-hours-before checklist that calms nerves and ticks every box.",
  },
  {
    icon: Users,
    title: "WhatsApp Success Community",
    desc: "Free access to our circle of 1,500+ approved students. Recent interview debriefs, slot tips, real-time moral support.",
    free: true,
  },
  {
    icon: Headphones,
    title: "Post-Interview Debrief",
    desc: "Whatever the outcome, we sit with you after - celebrate, or plan the next move. Coaching doesn't stop at the consulate gate.",
  },
];

const process = [
  {
    icon: PhoneCall,
    title: "Discovery chat",
    body: "A quick WhatsApp chat. We learn your timeline, university, consulate, and concerns - then recommend the right plan.",
  },
  {
    icon: FileCheck2,
    title: "Profile & DS-160 deep dive",
    body: "Your Specialist reviews every field, your I-20, SEVIS receipt, sponsor docs, and travel history. You get a written report of what to fix.",
  },
  {
    icon: MessageCircle,
    title: "Mock interviews",
    body: "Multiple sessions tuned to your consulate's style. Each ends with line-by-line feedback and a list of what to rehearse next.",
  },
  {
    icon: Target,
    title: "Final interview-day prep",
    body: "24 hours before, we lock in your opening line, document order, body language, and answers to officer-logic curveballs.",
  },
  {
    icon: HeartHandshake,
    title: "Post-interview debrief",
    body: "Approved? We celebrate. Refused? Same day, we plan the re-attempt. You're not alone after the interview either.",
  },
];

const offer = [
  { icon: Globe2, label: "Online, anywhere in India" },
  { icon: Calendar, label: "Flexible IST scheduling" },
  { icon: Clock, label: "Same-week slots available" },
  { icon: ShieldCheck, label: "Transparent refund policy" },
];

function ServicesPage() {
  const [signupOpen, setSignupOpen] = useState(false);
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-hero">
        <div className="mx-auto max-w-6xl px-5 pt-16 pb-14 md:pt-24">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Services</p>
          <h1 className="mt-2 font-display text-5xl md:text-6xl font-semibold tracking-tight text-balance max-w-3xl">
            Everything you need between today and your US visa interview.
          </h1>
          <p className="mt-5 text-lg text-foreground/75 max-w-2xl leading-relaxed">
            Mock interviews, DS-160 review, refusal recovery, and a free community of approved students - all delivered
            1-on-1 by Specialists who&apos;ve sat in your seat.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              See plans &amp; pricing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {offer.map((o) => (
              <div
                key={o.label}
                className="flex items-center gap-2.5 rounded-2xl border border-border bg-card/70 px-4 py-3"
              >
                <o.icon className="h-4 w-4 text-primary shrink-0" />
                <span className="text-xs sm:text-sm text-foreground/80 font-medium">{o.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we offer - service grid */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="max-w-2xl">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">What we offer</p>
          <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-balance">
            Eight focused services. One outcome - approved.
          </h2>
          <p className="mt-4 text-foreground/70 leading-relaxed">
            Pick à la carte, or bundle them inside a Basic, Full, or Refusal Support plan. Every service is 1-on-1 with
            a real Specialist - never a pre-recorded course.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s) => (
            <div
              key={s.title}
              className="rounded-3xl bg-card border border-border p-7 hover:shadow-soft transition flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="h-12 w-12 rounded-2xl bg-accent grid place-items-center text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                {s.free && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold border border-emerald-200">
                    Free
                  </span>
                )}
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed flex-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works - process */}
      <section className="bg-ink text-ink-foreground">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <div className="max-w-2xl">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">How it works</p>
            <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-balance">
              From DS-160 to the consulate gate, in 5 steps.
            </h2>
            <p className="mt-4 text-ink-foreground/75 leading-relaxed">
              Every plan follows the same proven sequence. We tune the depth and number of mocks to your timeline.
            </p>
          </div>

          <ol className="mt-12 grid gap-4 md:gap-5">
            {process.map((step, i) => (
              <li
                key={step.title}
                className="grid grid-cols-[auto_1fr] gap-5 rounded-2xl bg-white/[0.04] border border-white/10 p-5 sm:p-6"
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-semibold">
                    {i + 1}
                  </span>
                  <step.icon className="h-4 w-4 text-ink-foreground/50" />
                </div>
                <div>
                  <h3 className="font-display text-lg sm:text-xl font-semibold">{step.title}</h3>
                  <p className="mt-1.5 text-sm text-ink-foreground/75 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Free WhatsApp community CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-hero p-7 sm:p-10 md:p-12">
          <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-border text-xs">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <span className="text-foreground/70">Free · Community-led</span>
              </div>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-balance">
                Not ready to enroll? Join the free WhatsApp circle.
              </h2>
              <p className="mt-3 text-foreground/70 max-w-xl leading-relaxed">
                <span className="font-medium text-foreground">1,500+ approved students</span> sharing recent interview
                debriefs, slot tips, and real-time Q&amp;A. No fee. Just join in.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSignupOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition whitespace-nowrap"
            >
              <MessageCircle className="h-4 w-4" /> Join the WhatsApp group
            </button>
          </div>
        </div>
      </section>

      <GroupSignupDialog open={signupOpen} onOpenChange={setSignupOpen} />

      {/* What strong prep looks like */}
      <section className="bg-ink text-ink-foreground">
        <div className="mx-auto max-w-4xl px-5 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">The bar we hold you to</p>
            <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-balance">
              What strong prep looks like.
            </h2>
            <p className="mt-4 text-ink-foreground/75 leading-relaxed">
              By interview day, every student we Specialist hits all six. That&apos;s the bar.
            </p>
          </div>
          <ol className="mt-12 grid gap-4">
            {[
              "Clear story - your program, university, and career path explained in 30 seconds, no rambling.",
              "Tight DS-160 - every field consistent with what you'll say at the window. Zero contradictions.",
              "Sharp answers - short, confident, specific. No memorised scripts, no over-explaining.",
              "Calm under pressure - you've practised curveballs, so unexpected questions don't shake you.",
              "Strong financials story - sponsor, funds, and intent to return, all clean and easy to verify.",
              "Interview-day ready - documents in order, opening line rehearsed, body language relaxed.",
            ].map((step, i) => (
              <li key={i} className="flex gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                <span className="h-8 w-8 shrink-0 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-semibold">
                  {i + 1}
                </span>
                <span className="text-ink-foreground/85 pt-1">{step}</span>
              </li>
            ))}
          </ol>

          <div className="mt-12 text-center">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium hover:opacity-90 transition"
            >
              Pick your plan <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
